import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import {
  AlertCircle,
  CalendarClock,
  Clock3,
  Sparkles,
  Users,
  Wallet,
} from "lucide-react";
import { MAIN_URL, getApiHeaders } from "../../Configurations/Urls";
import useAuthStore from "../../Zustand/Store/useAuthStore";
import { resolveUserRoleLabel } from "../../Utils/resolveUserRoleLabel";

export function isCounsellorUser(userData) {
  const roleName = String(resolveUserRoleLabel(userData) || "")
    .trim()
    .toLowerCase();
  return roleName === "counselor" || roleName === "counsellor";
}

const sameCalendarDay = (value, day) => {
  if (!value) return false;
  const parsed = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(parsed.getTime())) return false;
  return (
    parsed.getFullYear() === day.getFullYear() &&
    parsed.getMonth() === day.getMonth() &&
    parsed.getDate() === day.getDate()
  );
};

const isTodayLead = (row, day, newLeadIds) =>
  newLeadIds.has(row.organization_ems_lead_id) ||
  sameCalendarDay(row.lead_datetime, day) ||
  sameCalendarDay(row.next_follow_up, day);

const missedRows = (data) => data?.missed_follow_ups || [];

const followUpRows = (data) => [
  ...(data?.follow_up_due_today || []),
  ...(data?.upcoming_follow_up || []),
];

const FOLLOW_TABS = [
  {
    id: "follow",
    title: "Follow up",
    rows: followUpRows,
    empty: "No follow-ups due today or later.",
    tone: "today",
    status: "Scheduled",
    Icon: CalendarClock,
  },
  {
    id: "missed",
    title: "Missed follow-ups",
    rows: missedRows,
    empty: "No missed follow-ups. You are on track.",
    tone: "missed",
    status: "Missed",
    Icon: AlertCircle,
  },
];

const TONES = {
  new: { accent: "#0891B2", tint: "#F0FDFA" },
  open: { accent: "#4f46e5", tint: "#eef2ff" },
  due: { accent: "#d97706", tint: "#fffbeb" },
  overdue: { accent: "#dc2626", tint: "#fef2f2" },
  missed: { accent: "#dc2626", tint: "#fff7f7" },
  late: { accent: "#c2410c", tint: "#fff7ed" },
  today: { accent: "#0f766e", tint: "#f0fdfa" },
  upcoming: { accent: "#15803d", tint: "#f0fdf4" },
};

const formatDueParts = (value) => {
  if (!value) return null;
  const parsed = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(parsed.getTime())) return { date: String(value), time: "" };
  const date = `${String(parsed.getDate()).padStart(2, "0")}-${String(parsed.getMonth() + 1).padStart(2, "0")}-${parsed.getFullYear()}`;
  const time = parsed.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" });
  return { date, time };
};

const formatClock = (value) => {
  if (!value) return "";
  const match = String(value).match(/^(\d{1,2}):(\d{2})/);
  if (!match) return String(value);
  const date = new Date();
  date.setHours(Number(match[1]), Number(match[2]), 0, 0);
  return date.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" });
};

const toMinutes = (value) => {
  if (!value) return null;
  const match = String(value).match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
};

const clockInZone = (timeZone, at) => {
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(at));
  } catch {
    return null;
  }
};

const zonedWallToDate = (timeZone, hms, ymd) => {
  const match = String(hms || "").match(/^(\d{1,2}):(\d{2})/);
  if (!match || !timeZone) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  try {
    const dateYmd =
      ymd ||
      new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(new Date());
    let utc = new Date(
      `${dateYmd}T${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}:00Z`
    );
    for (let i = 0; i < 4; i += 1) {
      const parts = new Intl.DateTimeFormat("en-GB", {
        timeZone,
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
      }).formatToParts(utc);
      const wallHour = Number(parts.find((part) => part.type === "hour")?.value);
      const wallMinute = Number(parts.find((part) => part.type === "minute")?.value);
      if (Number.isNaN(wallHour) || Number.isNaN(wallMinute)) return null;
      let diff = hour * 60 + minute - (wallHour * 60 + wallMinute);
      if (diff > 12 * 60) diff -= 24 * 60;
      if (diff < -12 * 60) diff += 24 * 60;
      if (diff === 0) return utc;
      utc = new Date(utc.getTime() + diff * 60000);
    }
    return utc;
  } catch {
    return null;
  }
};

const formatIst = (date) => {
  if (!date) return "";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Kolkata",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const zoneCalendar = (timeZone, at) => {
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(new Date(at));
    const pick = (type) => Number(parts.find((part) => part.type === type)?.value);
    const year = pick("year");
    const month = pick("month");
    const day = pick("day");
    const hour = pick("hour");
    const minute = pick("minute");
    if ([year, month, day, hour, minute].some((value) => Number.isNaN(value))) return null;
    return { year, month, day, minutes: hour * 60 + minute };
  } catch {
    return null;
  }
};

const shiftYmd = ({ year, month, day }, offset) => {
  const date = new Date(Date.UTC(year, month - 1, day + offset));
  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
};

const callWindows = (row) =>
  [
    [row.morning_start || "07:00", row.morning_end || "09:00"],
    [row.evening_start || "19:00", row.evening_end || "21:00"],
  ]
    .map(([start, end]) => ({ start, end, startMin: toMinutes(start), endMin: toMinutes(end) }))
    .filter((window) => window.startMin != null && window.endMin != null)
    .sort((a, b) => a.startMin - b.startMin);

const counsellorOverlap = (winStart, winEnd, counsellorStart, counsellorEnd) => {
  const startCal = zoneCalendar("Asia/Kolkata", winStart);
  if (!startCal) return null;
  let best = null;
  for (let offset = -1; offset <= 2; offset += 1) {
    const ymd = shiftYmd(startCal, offset);
    const from = zonedWallToDate("Asia/Kolkata", counsellorStart, ymd);
    const to = zonedWallToDate("Asia/Kolkata", counsellorEnd, ymd);
    if (!from || !to) continue;
    const start = Math.max(winStart.getTime(), from.getTime());
    const end = Math.min(winEnd.getTime(), to.getTime());
    if (end <= start) continue;
    if (!best || start < best.start) best = { start, end };
  }
  return best;
};

const formatRange = (from, to, suffix) => {
  if (!from || !to) return "";
  return `${from} – ${to}${suffix ? ` ${suffix}` : ""}`;
};

const nextCallWindow = (row, counsellorStart, counsellorEnd) => {
  const windows = callWindows(row);
  const anchorRaw = row.last_activity_datetime || row.lead_datetime;
  const anchor = anchorRaw ? new Date(String(anchorRaw).replace(" ", "T")) : new Date();
  if (Number.isNaN(anchor.getTime()) || windows.length === 0) return null;
  const zone = row.local_timezone;
  const local = zone ? zoneCalendar(zone, anchor) : null;
  const startTime = counsellorStart || "09:00";
  const endTime = counsellorEnd || "21:00";
  if (!zone || !local) return null;

  for (let day = 0; day < 4; day += 1) {
    const ymd = shiftYmd(local, day);
    for (let index = 0; index < windows.length; index += 1) {
      const window = windows[index];
      const winStart = zonedWallToDate(zone, window.start, ymd);
      const winEnd = zonedWallToDate(zone, window.end, ymd);
      if (!winStart || !winEnd || winEnd.getTime() <= anchor.getTime()) continue;
      const overlap = counsellorOverlap(winStart, winEnd, startTime, endTime);
      if (!overlap || overlap.end <= anchor.getTime()) continue;
      const ist = formatRange(formatIst(overlap.start), formatIst(overlap.end));
      const lead = formatRange(
        clockInZone(zone, overlap.start),
        clockInZone(zone, overlap.end)
      );
      if (ist) return { ist, lead };
    }
  }
  return null;
};

const greetingForNow = () => {
  const hour = Number(
    new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      hourCycle: "h23",
    }).format(new Date())
  );
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
};

function CountCard({ label, value, hint, onClick, tone, Icon }) {
  const palette = TONES[tone] || TONES.new;
  return (
    <button
      type="button"
      className="ems-csl-kpi"
      onClick={onClick}
      style={{ "--csl-accent": palette.accent, "--csl-tint": palette.tint }}
    >
      <div className="ems-csl-kpi-top">
        <div className="ems-csl-kpi-label">{label}</div>
        <div className="ems-csl-kpi-icon">
          <Icon size={16} />
        </div>
      </div>
      <div className="ems-csl-kpi-value">{value}</div>
      {hint ? <div className="ems-csl-kpi-hint">{hint}</div> : null}
    </button>
  );
}

function LeadList({
  title,
  rows,
  empty,
  onOpen,
  onSnapshot,
  tone,
  Icon,
  hideHead = false,
  counsellorStart,
  counsellorEnd,
  status,
}) {
  const palette = TONES[tone] || TONES.today;

  const sortedRows = useMemo(() => {
    const dueTime = (value) => {
      if (!value) return Number.MAX_SAFE_INTEGER;
      const parsed = new Date(String(value).replace(" ", "T")).getTime();
      return Number.isNaN(parsed) ? Number.MAX_SAFE_INTEGER : parsed;
    };

    const today = new Date();
    return [...rows].sort((a, b) => {
      const aDue = dueTime(a.next_follow_up);
      const bDue = dueTime(b.next_follow_up);
      const aMissing = aDue === Number.MAX_SAFE_INTEGER;
      const bMissing = bDue === Number.MAX_SAFE_INTEGER;
      if (aMissing !== bMissing) return aMissing ? 1 : -1;
      const aToday = sameCalendarDay(a.next_follow_up, today) ? 0 : 1;
      const bToday = sameCalendarDay(b.next_follow_up, today) ? 0 : 1;
      if (aToday !== bToday) return aToday - bToday;
      return aDue - bDue;
    });
  }, [rows]);

  return (
    <section
      className="ems-csl-queue"
      style={{ "--csl-accent": palette.accent, "--csl-tint": palette.tint }}
    >
      {hideHead ? null : (
        <div className="ems-csl-queue-head">
          <div className="ems-csl-kpi-icon">
            <Icon size={16} />
          </div>
          <h2>{title}</h2>
          <span className="ems-csl-count">{rows.length}</span>
        </div>
      )}
      <div className="ems-csl-queue-body ems-csl-queue-body-list">
        {sortedRows.length === 0 ? (
          <div className="ems-csl-empty">{empty}</div>
        ) : (
          <table className="ems-csl-table">
            <thead>
              <tr>
                <th>Due Date</th>
                <th>Time to call</th>
                <th>Country</th>
                <th>Phone</th>
                <th>Lead</th>
                <th>Status</th>
                <th>Snapshot</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row) => {
                const nextCall = nextCallWindow(row, counsellorStart, counsellorEnd);
                const due = formatDueParts(row.next_follow_up);
                return (
                  <tr
                    key={`${title}-${row.organization_ems_lead_id}`}
                    onClick={() => onOpen(row.organization_ems_lead_id)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onOpen(row.organization_ems_lead_id);
                      }
                    }}
                    tabIndex={0}
                  >
                    <td>
                      {due ? (
                        <div className="ems-csl-due">
                          <b>{due.date}</b>
                          {due.time ? <span className="ems-csl-last-when">{due.time}</span> : null}
                        </div>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      <div className="ems-csl-call">
                        {nextCall?.ist ? <span>{nextCall.ist}</span> : "—"}
                        {nextCall?.lead ? (
                          <span className="ems-csl-last-when">Lead time {nextCall.lead}</span>
                        ) : null}
                      </div>
                    </td>
                    <td>{row.country_name || "—"}</td>
                    <td>{row.phone || "—"}</td>
                    <td>
                      <div className="ems-csl-lead-cell">
                        <div>
                          <div className="ems-csl-lead-name">
                            {row.person_full_name || "Lead"}
                          </div>
                          {row.is_late_follow_up ? (
                            <span className="ems-csl-late-tag">Late follow-up</span>
                          ) : null}
                          <div className="ems-csl-lead-program">
                            {row.training_program_name || "—"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      {status ? (
                        <span className={`ems-csl-status is-${tone}`}>{status}</span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="ems-csl-snapshot"
                        onClick={(event) => {
                          event.stopPropagation();
                          onSnapshot(row.organization_ems_lead_id);
                        }}
                      >
                        Snapshot
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

export default function CounsellorDashboard() {
  const navigate = useNavigate();
  const { userData } = useAuthStore();
  const organizationId = userData?.organization?.organization_id;
  const organizationUserId =
    userData?.organization_user_id ??
    userData?.organization_user?.organization_user_id ??
    userData?.user_active_organization?.organization_user_id ??
    null;
  const counsellorName =
    userData?.user_name || userData?.full_name || userData?.person_full_name || "";
  const counsellorScope = isCounsellorUser(userData);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [followTab, setFollowTab] = useState("follow");
  const [todayOnly, setTodayOnly] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  const [openLeadIds, setOpenLeadIds] = useState(null);

  useEffect(() => {
    if (!organizationId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    axios
      .get(`${MAIN_URL}/api/ems/counsellor-dashboard`, {
        params: {
          organization_id: organizationId,
          ...(counsellorScope && organizationUserId
            ? { organization_user_id: organizationUserId }
            : {}),
        },
        headers: getApiHeaders(),
      })
      .then((res) => {
        if (!cancelled) setData(res.data?.data || null);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err?.response?.data?.message || "Failed to load counsellor dashboard."
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    axios
      .post(
        `${MAIN_URL}/api/ems/leads/filtered`,
        {
          organization_id: organizationId,
          lead_status: "open",
          page: 1,
          pageSize: 2000,
          sortBy: "lead_datetime",
          sortOrder: "desc",
        },
        { headers: getApiHeaders() }
      )
      .then((res) => {
        const ids = (res.data?.data || [])
          .map((row) => row.organization_ems_lead_id)
          .filter((id) => id != null);
        if (!cancelled) setOpenLeadIds(new Set(ids));
      })
      .catch(() => {
        if (!cancelled) setOpenLeadIds(null);
      });

    return () => {
      cancelled = true;
    };
  }, [organizationId, organizationUserId, counsellorScope]);

  const onlyOpen = (rows) =>
    (rows || []).filter((row) => {
      if (row.lead_status_open === false || row.lead_status_open === 0) return false;
      if (row.lead_status_open === true || row.lead_status_open === 1) return true;
      if (!openLeadIds) return true;
      return openLeadIds.has(row.organization_ems_lead_id);
    });

  const openLead = (id) =>
    navigate(`/lead-activities?organization_ems_lead_id=${encodeURIComponent(String(id))}`);
  const openSnapshot = (id) =>
    navigate(`/lead-activities/snapshot/${encodeURIComponent(String(id))}`);
  const openLeads = () =>
    navigate("/leads?lead_status=open&page=1&pageSize=25&sortBy=lead_datetime&sortOrder=desc");
  const showTodaysLeads = () => {
    const day = new Date();
    const newLeadIds = new Set(
      (data?.new_leads || []).map((row) => row.organization_ems_lead_id)
    );
    const inFollowUp = followUpRows(data).some((row) => isTodayLead(row, day, newLeadIds));
    setTodayOnly(true);
    setFollowTab(inFollowUp ? "follow" : "missed");
    document.getElementById("ems-csl-queues")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const openFeesDueThisMonth = () =>
    navigate(
      "/fee-installments?status=pending&due_period=this_month&page=1&pageSize=25&sortBy=due_date&sortOrder=asc"
    );
  const openFeesOverdue = () =>
    navigate("/fee-installments?period=overdue&page=1&pageSize=25&sortBy=due_date&sortOrder=asc");
  const counts = data?.counts || {};
  const today = new Date();
  const hoursLabel =
    data?.counsellor_start_time && data?.counsellor_end_time
      ? `${formatClock(data.counsellor_start_time)} – ${formatClock(data.counsellor_end_time)} IST`
      : null;

  return (
    <div className="ems-dashboard ems-fade-up ems-csl">
      <div className="ems-csl-hero">
        <div>
          <h1 className="ems-page-title">
            {counsellorScope ? "Counsellor workspace" : "Counsellor report"}
          </h1>
          <div className="ems-page-sub">
            {greetingForNow()}
            {counsellorName ? `, ${counsellorName}` : ""}
          </div>
        </div>
        <div className="ems-csl-hero-side">
          {hoursLabel ? (
            <div className="ems-csl-hours">
              <Clock3 size={13} />
              Working hours {hoursLabel}
            </div>
          ) : null}
          <div className="ems-csl-datecard">
            <strong>{String(today.getDate()).padStart(2, "0")}</strong>
            <span>
              {today.toLocaleDateString("en-IN", { month: "short", year: "numeric" })}
              {" · "}
              {today.toLocaleDateString("en-IN", { weekday: "long" })}
            </span>
          </div>
        </div>
      </div>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <>
          <div className="ems-csl-kpis">
            <CountCard
              label="New leads"
              value={counts.new_leads ?? 0}
              hint="Awaiting first contact"
              onClick={showTodaysLeads}
              tone="new"
              Icon={Sparkles}
            />
            <CountCard
              label="Open leads"
              value={counts.opened ?? 0}
              hint="Active in pipeline"
              onClick={openLeads}
              tone="open"
              Icon={Users}
            />
            <CountCard
              label="Fees due this month"
              value={counts.fees_due_this_month ?? 0}
              hint="Pending collections"
              onClick={openFeesDueThisMonth}
              tone="due"
              Icon={Wallet}
            />
            <CountCard
              label="Fees overdue"
              value={counts.fees_overdue ?? 0}
              hint="Needs follow-up"
              onClick={openFeesOverdue}
              tone="overdue"
              Icon={AlertCircle}
            />
          </div>

          <div className="ems-csl-follow" id="ems-csl-queues">
            {todayOnly ? (
              <div className="ems-csl-filter">
                <span>Today’s leads</span>
                <button type="button" onClick={() => setTodayOnly(false)}>
                  Clear
                </button>
              </div>
            ) : null}
            <div className="ems-csl-tabbar">
            <div className="ems-csl-tabs" role="tablist">
              {FOLLOW_TABS.map((tab) => {
                const day = new Date();
                const newLeadIds = new Set(
                  (data?.new_leads || []).map((row) => row.organization_ems_lead_id)
                );
                const source = showClosed ? tab.rows(data) : onlyOpen(tab.rows(data));
                const rows = todayOnly
                  ? source.filter((row) => isTodayLead(row, day, newLeadIds))
                  : source;
                const count = rows.length;
                const active = followTab === tab.id;
                const palette = TONES[tab.tone];
                const TabIcon = tab.Icon;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    className={`ems-csl-tab${active ? " is-active" : ""}`}
                    style={{ "--csl-accent": palette.accent, "--csl-tint": palette.tint }}
                    onClick={() => setFollowTab(tab.id)}
                  >
                    <TabIcon size={15} />
                    <span>{tab.title}</span>
                    <b>{count}</b>
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              className={`ems-csl-closed-toggle${showClosed ? " is-on" : ""}`}
              onClick={() => setShowClosed((current) => !current)}
            >
              {showClosed ? "Hide closed" : "Closed leads"}
            </button>
            </div>
            {FOLLOW_TABS.filter((tab) => tab.id === followTab).map((tab) => {
              const day = new Date();
              const newLeadIds = new Set(
                (data?.new_leads || []).map((row) => row.organization_ems_lead_id)
              );
              const source = showClosed ? tab.rows(data) : onlyOpen(tab.rows(data));
              const rows = todayOnly
                ? source.filter((row) => isTodayLead(row, day, newLeadIds))
                : source;
              return (
              <LeadList
                key={tab.id}
                title={tab.title}
                rows={rows}
                empty={todayOnly ? "No today’s leads in this list." : tab.empty}
                onOpen={openLead}
                onSnapshot={openSnapshot}
                tone={tab.tone}
                Icon={tab.Icon}
                hideHead
                status={tab.status}
                counsellorStart={data?.counsellor_start_time}
                counsellorEnd={data?.counsellor_end_time}
              />
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
